import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { X, GripVertical, ChevronRight, ChevronLeft, ChevronDown, Check, ChevronsUpDown, ChevronsDownUp, Sparkles, Loader2, Plus, FileText, Eye, Maximize2, Minimize2, Link2, Pencil, MoreVertical, Pin, Search, Layers, BookOpen } from 'lucide-react';
import { MindMapNode } from '../types';
import { parseMarkdown } from '../utils/markdownUtils';
import { getAdjacentNoteIds } from '../utils/noteSequence';
import { NodeImageSlider } from './NodeImageSlider';
import { useIsMobile } from '../hooks/useIsMobile';

// Larger touch-friendly icon sizes on mobile (~Material Design's 44-48dp target),
// falling back to the original compact desktop sizes.
const iconSize = (isMobile: boolean, desktopSize: number, mobileSize = 20) => isMobile ? mobileSize : desktopSize;

interface TreeNode {
    node: MindMapNode;
    children: TreeNode[];
    depth: number;
}

function flattenVisible(nodes: TreeNode[], collapsedIds: Set<string>): string[] {
    const result: string[] = [];
    for (const n of nodes) {
        result.push(n.node.id);
        if (!collapsedIds.has(n.node.id) && n.children.length > 0) {
            result.push(...flattenVisible(n.children, collapsedIds));
        }
    }
    return result;
}

function buildTree(nodes: MindMapNode[], parentId: string | null = null, depth = 0): TreeNode[] {
    if (parentId === null && depth === 0) {
        // Find root nodes: no parentId, empty parentId, or parentId pointing to non-existent node
        const nodeIds = new Set(nodes.map(n => n.id));
        const roots = nodes.filter(n => !(n.parentId) || !nodeIds.has(n.parentId));
        return roots.map(n => ({
            node: n,
            children: buildTree(nodes, n.id, depth + 1),
            depth,
        }));
    }
    return nodes
        .filter(n => n.parentId === parentId)
        .map(n => ({
            node: n,
            children: buildTree(nodes, n.id, depth + 1),
            depth,
        }));
}

interface DragState {
    draggedId: string;
    draggedIds: string[]; // All nodes being moved (multi-select)
    overId: string | null;
    overPosition: 'before' | 'after' | 'onto' | null;
}

interface OutlineItemProps {
    treeNode: TreeNode;
    collapsedIds: Set<string>;
    onToggleCollapse: (id: string) => void;
    onToggleCollapseAll: (id: string) => void;
    generatingIds: Set<string>;
    dragState: DragState | null;
    isRearrangeMode: boolean;
    selectedIds: Set<string>;
    onToggleSelect: (id: string) => void;
    onDragStart: (id: string) => void;
    onDragOver: (id: string, position: 'before' | 'after' | 'onto') => void;
    onDrop: () => void;
    onDragEnd: () => void;
    onFocusNode: (id: string) => void;
    onExpandNode?: (id: string) => void;
    onContextMenu: (id: string, x: number, y: number) => void;
    activeNodeId?: string | null;
    onHoverEnter: (node: MindMapNode, x: number, y: number) => void;
    onHoverLeave: () => void;
    linkMode?: boolean;
    onLinkSelect?: (nodeId: string) => void;
    onCancelLinkMode?: () => void;
    editingId: string | null;
    editingTitle: string;
    onStartEdit: (id: string, currentTitle: string) => void;
    onEditChange: (val: string) => void;
    onEditCommit: () => void;
    onEditCancel: () => void;
    filterQuery?: string;
    isMobile: boolean;
}

const OutlineItem: React.FC<OutlineItemProps> = ({
    treeNode,
    collapsedIds,
    onToggleCollapse,
    onToggleCollapseAll,
    generatingIds,
    dragState,
    isRearrangeMode,
    selectedIds,
    onToggleSelect,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    onFocusNode,
    onExpandNode,
    onContextMenu,
    activeNodeId,
    onHoverEnter,
    onHoverLeave,
    linkMode = false,
    onLinkSelect,
    onCancelLinkMode,
    editingId,
    editingTitle,
    onStartEdit,
    onEditChange,
    onEditCommit,
    onEditCancel,
    isMobile,
    filterQuery,
}) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const clickTimerRef = useRef<NodeJS.Timeout | null>(null);
    const collapsed = collapsedIds.has(treeNode.node.id);
    const isGenerating = generatingIds.has(treeNode.node.id);
    const isActive = treeNode.node.id === activeNodeId;

    useEffect(() => {
        if (isActive && itemRef.current) {
            itemRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [isActive]);

    const hasChildren = treeNode.children.length > 0;
    const isDragging = dragState ? dragState.draggedIds.includes(treeNode.node.id) : false;
    const isSelected = selectedIds.has(treeNode.node.id);
    const isOverBefore = dragState?.overId === treeNode.node.id && dragState?.overPosition === 'before';
    const isOverAfter = dragState?.overId === treeNode.node.id && dragState?.overPosition === 'after';
    const isOverOnto = dragState?.overId === treeNode.node.id && dragState?.overPosition === 'onto';
    // Mobile screens are narrow, so nested items get a shallower, tighter indent than desktop.
    const indentStep = isMobile ? 10 : 16;
    const maxIndentDepth = isMobile ? 4 : 5;
    const indentPx = Math.min(treeNode.depth, maxIndentDepth) * indentStep + 8;

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        if (!itemRef.current || (dragState && dragState.draggedIds.includes(treeNode.node.id))) return;
        const rect = itemRef.current.getBoundingClientRect();
        const pct = (e.clientY - rect.top) / rect.height;
        // Top/bottom 25% = reorder before/after; middle 50% = reparent onto
        const position: 'before' | 'after' | 'onto' = pct < 0.25 ? 'before' : pct > 0.75 ? 'after' : 'onto';
        onDragOver(treeNode.node.id, position);
    };

    return (
        <div>
            <div ref={itemRef} className={`relative ${isDragging ? 'opacity-40' : ''}`}>
                {isOverBefore && (
                    <div
                        className="absolute top-0 left-0 right-0 h-0.5 bg-blue-500 z-10 pointer-events-none rounded-full"
                        style={{ marginLeft: indentPx + 20 }}
                    />
                )}
                <div
                    className={`flex items-center gap-2 md:gap-1 py-2.5 md:py-1 rounded-lg select-none transition-colors group ${
                        isOverOnto ? 'ring-2 ring-blue-500 ring-inset bg-blue-50/50 dark:bg-blue-900/20' :
                        linkMode ? 'cursor-pointer hover:bg-teal-50 dark:hover:bg-teal-900/30' :
                        `cursor-default ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''} ${isActive ? 'bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/40' : 'hover:bg-gray-100 dark:hover:bg-slate-700'}`
                    }`}
                    style={{ paddingLeft: `${indentPx}px`, paddingRight: '8px' }}
                    draggable={treeNode.depth > 0}
                    onDragStart={(e) => {
                        if (treeNode.depth === 0) { e.preventDefault(); return; }
                        e.dataTransfer.effectAllowed = 'move';
                        onDragStart(treeNode.node.id);
                    }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => { e.preventDefault(); onDrop(); }}
                    onDragEnd={onDragEnd}
                    onContextMenu={(e) => { e.preventDefault(); onContextMenu(treeNode.node.id, e.clientX, e.clientY); }}
                >
                    {isRearrangeMode && (
                        <input
                            type="checkbox"
                            className={`shrink-0 w-3.5 h-3.5 rounded accent-blue-500 cursor-pointer transition-opacity ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-80'}`}
                            checked={isSelected}
                            onChange={() => onToggleSelect(treeNode.node.id)}
                            onClick={e => e.stopPropagation()}
                            onMouseDown={e => e.stopPropagation()}
                        />
                    )}
                    {treeNode.depth > 0 && (
                        <GripVertical
                            size={iconSize(isMobile, 14)}
                            className="text-gray-300 group-hover:text-gray-400 dark:group-hover:text-gray-500 shrink-0 cursor-grab"
                        />
                    )}
                    {hasChildren ? (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (clickTimerRef.current) {
                                    clearTimeout(clickTimerRef.current);
                                    clickTimerRef.current = null;
                                    onToggleCollapseAll(treeNode.node.id);
                                } else {
                                    clickTimerRef.current = setTimeout(() => {
                                        clickTimerRef.current = null;
                                        onToggleCollapse(treeNode.node.id);
                                    }, 220);
                                }
                            }}
                            className="p-1.5 md:p-0.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 active:bg-gray-200 dark:active:bg-slate-600 active:scale-95 transition-colors shrink-0"
                            title={collapsed ? 'Click: mở 1 cấp | Double-click: mở tất cả' : 'Click: đóng 1 cấp | Double-click: đóng tất cả'}
                        >
                            {collapsed ? <ChevronRight size={iconSize(isMobile, 14)} /> : <ChevronDown size={iconSize(isMobile, 14)} />}
                        </button>
                    ) : (
                        <span className="w-[18px] shrink-0" />
                    )}
                    <button
                        onMouseEnter={(e) => onHoverEnter(treeNode.node, e.clientX, e.clientY)}
                        onMouseLeave={onHoverLeave}
                        onClick={(e) => { e.stopPropagation(); onHoverEnter(treeNode.node, e.clientX, e.clientY); }}
                        className="p-1.5 md:p-0.5 rounded-md text-gray-300 hover:text-blue-400 dark:text-gray-600 dark:hover:text-blue-400 active:bg-blue-50 dark:active:bg-blue-900/30 active:scale-95 shrink-0 transition-colors"
                        title="Xem nội dung"
                    >
                        <Eye size={iconSize(isMobile, 11)} />
                    </button>
                    {editingId === treeNode.node.id ? (
                        <input
                            autoFocus
                            className="flex-1 text-base md:text-sm bg-white dark:bg-slate-700 border border-blue-400 rounded px-1.5 py-0.5 outline-none text-gray-900 dark:text-gray-100 min-w-0"
                            value={editingTitle}
                            onChange={e => onEditChange(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') { e.preventDefault(); onEditCommit(); }
                                if (e.key === 'Escape') { e.preventDefault(); onEditCancel(); }
                            }}
                            onBlur={onEditCommit}
                            onClick={e => e.stopPropagation()}
                        />
                    ) : (
                        <span
                            className={`text-base md:text-sm truncate flex-1 leading-snug cursor-pointer flex items-center gap-1.5 ${linkMode ? 'text-gray-800 dark:text-gray-200 group-hover:text-teal-700 dark:group-hover:text-teal-300' : isActive ? 'text-blue-600 dark:text-blue-400 font-semibold' : 'text-gray-800 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400'}`}
                            onClick={(e) => {
                                if (e.detail === 2) {
                                    e.stopPropagation();
                                    if (onExpandNode) onExpandNode(treeNode.node.id);
                                    else onStartEdit(treeNode.node.id, treeNode.node.title || '');
                                    return;
                                }
                                if (linkMode) onLinkSelect?.(treeNode.node.id);
                                else onFocusNode(treeNode.node.id);
                            }}
                        >
                            {isGenerating
                                ? <><Loader2 size={11} className="animate-spin text-violet-400 shrink-0" /><span className="text-violet-400 italic">Generating...</span></>
                                : treeNode.node.title
                                    ? (filterQuery
                                        ? (() => {
                                            const title = treeNode.node.title!;
                                            const idx = title.toLowerCase().indexOf(filterQuery.toLowerCase());
                                            if (idx === -1) return <>{title}</>;
                                            return <>{title.slice(0, idx)}<mark className="bg-yellow-200 dark:bg-yellow-600 text-inherit rounded-sm not-italic">{title.slice(idx, idx + filterQuery.length)}</mark>{title.slice(idx + filterQuery.length)}</>;
                                        })()
                                        : treeNode.node.title)
                                    : <span className={`italic ${isActive ? 'text-blue-400 dark:text-blue-500' : 'text-gray-400'}`}>Untitled</span>
                            }
                        </span>
                    )}
                    {linkMode && <Link2 size={12} className="text-teal-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-auto" />}
                    {!linkMode && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onContextMenu(treeNode.node.id, e.clientX, e.clientY); }}
                            className="p-2 md:p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600 active:bg-gray-300 dark:active:bg-slate-500 active:scale-95 shrink-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-colors"
                            title="Menu"
                        >
                            <MoreVertical size={iconSize(isMobile, 15)} />
                        </button>
                    )}
                </div>
                {isOverAfter && (
                    <div
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 z-10 pointer-events-none rounded-full"
                        style={{ marginLeft: indentPx + 20 }}
                    />
                )}
            </div>
            {!collapsed && treeNode.children.map(child => (
                <OutlineItem
                    key={child.node.id}
                    treeNode={child}
                    collapsedIds={collapsedIds}
                    onToggleCollapse={onToggleCollapse}
                    onToggleCollapseAll={onToggleCollapseAll}
                    generatingIds={generatingIds}
                    dragState={dragState}
                    isRearrangeMode={isRearrangeMode}
                    selectedIds={selectedIds}
                    onToggleSelect={onToggleSelect}
                    onDragStart={onDragStart}
                    onFocusNode={onFocusNode}
                    onExpandNode={onExpandNode}
                    onDragOver={onDragOver}
                    onDrop={onDrop}
                    onDragEnd={onDragEnd}
                    onContextMenu={onContextMenu}
                    activeNodeId={activeNodeId}
                    onHoverEnter={onHoverEnter}
                    onHoverLeave={onHoverLeave}
                    linkMode={linkMode}
                    onLinkSelect={onLinkSelect}
                    onCancelLinkMode={onCancelLinkMode}
                    editingId={editingId}
                    editingTitle={editingTitle}
                    onStartEdit={onStartEdit}
                    onEditChange={onEditChange}
                    onEditCommit={onEditCommit}
                    onEditCancel={onEditCancel}
                    filterQuery={filterQuery}
                    isMobile={isMobile}
                />
            ))}
        </div>
    );
};

interface OutlinePanelProps {
    nodes: MindMapNode[];
    onApplyOrder: (orderedNodes: MindMapNode[]) => void;
    onFocusNode: (id: string) => void;
    onExpandNode?: (id: string) => void;
    onClose: () => void;
    onAutoTitle?: (id: string) => Promise<void>;
    onAutoTitleBranch?: (id: string) => Promise<void>;
    onCreateChildNote?: (parentId: string) => void;
    onRenameNode?: (id: string, newTitle: string) => void;
    activeNodeId?: string | null;
    language?: string;
    linkMode?: boolean;
    onLinkSelect?: (nodeId: string, notebookId?: string, nodeTitle?: string, notebookTitle?: string) => void;
    onCancelLinkMode?: () => void;
    initialCollapsedIds?: Set<string>;
    onCollapsedIdsChange?: (ids: Set<string>) => void;
    onCreateLink?: (targetId: string, notebookId?: string, nodeTitle?: string, notebookTitle?: string) => void;
    allNotebooks?: { id: string; title: string; nodes: MindMapNode[] }[];
    currentNotebookId?: string;
    onLoadNotebook?: (notebookId: string) => Promise<void>;
    // Linking two notes from the outline alone: right-click one → "Start a link", right-click
    // another → "Link here". The waiting start lives in the parent so it survives closing the panel.
    pendingLink?: { id: string; title: string } | null;
    onPendingLinkChange?: (p: { id: string; title: string } | null) => void;
    onLinkNotes?: (sourceId: string, targetId: string, targetNotebookId?: string, targetTitle?: string, targetNotebookTitle?: string) => void;
}

function collectParentIds(nodes: TreeNode[]): Set<string> {
    const ids = new Set<string>();
    const walk = (ns: TreeNode[]) => ns.forEach(n => { if (n.children.length > 0) { ids.add(n.node.id); walk(n.children); } });
    walk(nodes);
    return ids;
}

function collectSubtreeIds(rootId: string, nodes: TreeNode[]): { allIds: string[]; parentIds: string[] } {
    const allIds: string[] = [];
    const parentIds: string[] = [];
    const findAndCollect = (ns: TreeNode[]): boolean => {
        for (const n of ns) {
            if (n.node.id === rootId) {
                const walk = (node: TreeNode) => {
                    allIds.push(node.node.id);
                    if (node.children.length > 0) { parentIds.push(node.node.id); node.children.forEach(walk); }
                };
                walk(n);
                return true;
            }
            if (findAndCollect(n.children)) return true;
        }
        return false;
    };
    findAndCollect(nodes);
    return { allIds, parentIds };
}

function filterTree(nodes: TreeNode[], query: string): TreeNode[] {
    if (!query) return nodes;
    const q = query.toLowerCase();
    const filter = (ns: TreeNode[]): TreeNode[] =>
        ns.flatMap(n => {
            const filteredChildren = filter(n.children);
            const selfMatch = (n.node.title || '').toLowerCase().includes(q);
            if (selfMatch || filteredChildren.length > 0) return [{ ...n, children: filteredChildren }];
            return [];
        });
    return filter(nodes);
}

export const OutlinePanel: React.FC<OutlinePanelProps> = ({ nodes, onApplyOrder, onFocusNode, onExpandNode, onClose, onAutoTitle, onAutoTitleBranch, onCreateChildNote, onRenameNode, activeNodeId, language, linkMode = false, onLinkSelect, onCancelLinkMode, initialCollapsedIds, onCollapsedIdsChange, onCreateLink, allNotebooks, currentNotebookId, onLoadNotebook, pendingLink = null, onPendingLinkChange, onLinkNotes }) => {
    const isMobile = useIsMobile();
    const [localNodes, setLocalNodes] = useState<MindMapNode[]>(nodes);
    const [viewingNbId, setViewingNbId] = useState<string | null>(null);
    const [nbPickerOpen, setNbPickerOpen] = useState(false);
    const [loadingNbId, setLoadingNbId] = useState<string | null>(null);
    const [isDirty, setIsDirty] = useState(false);
    const [dragState, setDragState] = useState<DragState | null>(null);
    const [isRearrangeMode, setIsRearrangeMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [filterQuery, setFilterQuery] = useState('');
    const filterInputRef = useRef<HTMLInputElement>(null);
    const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() => initialCollapsedIds ? new Set(initialCollapsedIds) : new Set());
    const [outlineLevel, setOutlineLevel] = useState<'full' | 'partial' | 'collapsed'>('full');
    const [generatingIds, setGeneratingIds] = useState<Set<string>>(new Set());
    const [contextMenu, setContextMenu] = useState<{ id: string; x: number; y: number } | null>(null);
    const [menuPos, setMenuPos] = useState<{ left: number; top: number } | null>(null);
    const contextMenuRef = useRef<HTMLDivElement>(null);
    const [hoverPreview, setHoverPreview] = useState<{ node: MindMapNode; top: number; left: number } | null>(null);
    const [hoverExpanded, setHoverExpanded] = useState(false);
    const [pinnedPreviews, setPinnedPreviews] = useState<Array<{ id: string; node: MindMapNode; x: number; y: number; expanded: boolean }>>([]);
    const draggingPinRef = useRef<{ id: string; offsetX: number; offsetY: number } | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingTitle, setEditingTitle] = useState('');
    const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);
    const expandLockRef = useRef(false);

    // Global drag listeners for pinned preview windows
    useEffect(() => {
        const onMove = (e: MouseEvent) => {
            if (!draggingPinRef.current) return;
            const { id, offsetX, offsetY } = draggingPinRef.current;
            setPinnedPreviews(prev => prev.map(p => p.id === id ? { ...p, x: e.clientX - offsetX, y: e.clientY - offsetY } : p));
        };
        const onUp = () => { draggingPinRef.current = null; };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    }, []);

    const handleStartEdit = useCallback((id: string, currentTitle: string) => {
        setContextMenu(null);
        setEditingId(id);
        setEditingTitle(currentTitle);
    }, []);

    const handleEditChange = useCallback((val: string) => setEditingTitle(val), []);

    const handleEditCommit = useCallback(() => {
        if (editingId && onRenameNode) {
            onRenameNode(editingId, editingTitle.trim());
        }
        setEditingId(null);
        setEditingTitle('');
    }, [editingId, editingTitle, onRenameNode]);

    const handleEditCancel = useCallback(() => {
        setEditingId(null);
        setEditingTitle('');
    }, []);

    const handleItemHoverEnter = useCallback((node: MindMapNode, clientX: number, clientY: number) => {
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = setTimeout(() => setHoverPreview({ node, top: clientY, left: clientX }), 300);
    }, []);

    const handleItemHoverLeave = useCallback(() => {
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = setTimeout(() => setHoverPreview(null), 150);
    }, []);

    // Persist collapsed state to parent so it survives panel close/reopen
    useEffect(() => {
        onCollapsedIdsChange?.(collapsedIds);
    }, [collapsedIds]);

    // Sync external changes when not editing
    useEffect(() => {
        if (!isDirty) setLocalNodes(nodes);
    }, [nodes, isDirty]);

    // Auto-expand ancestors when active node changes
    useEffect(() => {
        if (!activeNodeId) return;
        // Build parent map
        const parentMap = new Map<string, string>();
        nodes.forEach(n => { if (n.parentId) parentMap.set(n.id, n.parentId); });
        // Collect ancestor chain
        const ancestors: string[] = [];
        let cur = parentMap.get(activeNodeId);
        while (cur) { ancestors.push(cur); cur = parentMap.get(cur); }
        if (ancestors.length === 0) return;
        setCollapsedIds(prev => {
            const next = new Set(prev);
            let changed = false;
            ancestors.forEach(id => { if (next.has(id)) { next.delete(id); changed = true; } });
            return changed ? next : prev;
        });
    }, [activeNodeId, nodes]);

    const viewingNb = viewingNbId ? (allNotebooks?.find(nb => nb.id === viewingNbId) ?? null) : null;
    const effectiveNodes = viewingNb ? viewingNb.nodes : localNodes;
    const vi = language === 'vietnamese';
    // Esc drops a waiting link (only while one is waiting, so Esc keeps its other jobs otherwise).
    useEffect(() => {
        if (!pendingLink || !onPendingLinkChange) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onPendingLinkChange(null); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [pendingLink, onPendingLinkChange]);
    const tree = buildTree(effectiveNodes);
    const activeTree = filterQuery ? filterTree(tree, filterQuery) : tree;
    const activeCollapsedIds = filterQuery ? new Set<string>() : collapsedIds;

    const visibleIds = useMemo(() => flattenVisible(activeTree, activeCollapsedIds), [activeTree, activeCollapsedIds]);

    const handleListKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown' && e.key !== 'Enter') return;
        e.preventDefault();
        e.stopPropagation();
        if (e.key === 'Enter' && activeNodeId && onExpandNode) {
            onExpandNode(activeNodeId);
            return;
        }
        if (visibleIds.length === 0) return;
        const currentIndex = activeNodeId ? visibleIds.indexOf(activeNodeId) : -1;
        const nextIndex = e.key === 'ArrowDown'
            ? Math.min(currentIndex + 1, visibleIds.length - 1)
            : Math.max(currentIndex - 1, 0);
        if (nextIndex !== currentIndex || currentIndex === -1) {
            onFocusNode(visibleIds[nextIndex === -1 ? 0 : nextIndex]);
        }
    }, [visibleIds, activeNodeId, onFocusNode, onExpandNode]);

    const handleToggleCollapse = useCallback((id: string) => {
        setCollapsedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    const handleToggleCollapseAll = useCallback((id: string) => {
        const { allIds, parentIds } = collectSubtreeIds(id, tree);
        setCollapsedIds(prev => {
            const isCollapsed = prev.has(id);
            const next = new Set(prev);
            if (isCollapsed) {
                allIds.forEach(i => next.delete(i));
            } else {
                parentIds.forEach(i => next.add(i));
            }
            return next;
        });
    }, [tree]);

    const getPartialCollapsed = useCallback((): Set<string> => {
        const result = new Set<string>();
        tree.forEach(root => {
            root.children.forEach(child => {
                if (child.children.length > 0) result.add(child.node.id);
            });
        });
        return result;
    }, [tree]);

    const handleExpandClick = useCallback(() => {
        if (outlineLevel === 'collapsed') {
            setOutlineLevel('partial');
            setCollapsedIds(getPartialCollapsed());
        } else {
            setOutlineLevel('full');
            setCollapsedIds(new Set());
        }
    }, [outlineLevel, getPartialCollapsed]);

    const handleCollapseClick = useCallback(() => {
        if (outlineLevel === 'full') {
            setOutlineLevel('partial');
            setCollapsedIds(getPartialCollapsed());
        } else {
            setOutlineLevel('collapsed');
            setCollapsedIds(collectParentIds(tree));
        }
    }, [outlineLevel, tree, getPartialCollapsed]);

    const handleExpandBranch = useCallback((id: string) => {
        setContextMenu(null);
        const { allIds } = collectSubtreeIds(id, tree);
        setCollapsedIds(prev => { const next = new Set(prev); allIds.forEach(i => next.delete(i)); return next; });
    }, [tree]);

    const handleCollapseBranch = useCallback((id: string) => {
        setContextMenu(null);
        const { parentIds } = collectSubtreeIds(id, tree);
        setCollapsedIds(prev => { const next = new Set(prev); parentIds.forEach(i => next.add(i)); return next; });
    }, [tree]);

    const handleOpenContextMenu = useCallback((id: string, x: number, y: number) => {
        setContextMenu({ id, x, y });
    }, []);

    // Keep the menu within the viewport — the mobile trigger sits at the right edge of the row,
    // so an unclamped menu would render partly off-screen there.
    useLayoutEffect(() => {
        if (!contextMenu) { setMenuPos(null); return; }
        const el = contextMenuRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        let left = contextMenu.x;
        let top = contextMenu.y;
        if (left + rect.width > window.innerWidth - 8) left = window.innerWidth - rect.width - 8;
        if (left < 8) left = 8;
        if (top + rect.height > window.innerHeight - 8) top = window.innerHeight - rect.height - 8;
        if (top < 8) top = 8;
        setMenuPos({ left, top });
    }, [contextMenu]);

    const handleAutoTitle = useCallback(async (id: string) => {
        if (!onAutoTitle) return;
        setContextMenu(null);
        setGeneratingIds(prev => new Set(prev).add(id));
        try {
            await onAutoTitle(id);
        } finally {
            setGeneratingIds(prev => { const next = new Set(prev); next.delete(id); return next; });
        }
    }, [onAutoTitle]);

    const handleAutoTitleBranch = useCallback(async (id: string) => {
        if (!onAutoTitleBranch) return;
        setContextMenu(null);
        // Only show spinner on descendants that don't already have a title
        const untitledDescendants = new Set<string>();
        const walk = (nodes: TreeNode[]) => nodes.forEach(n => {
            if (!n.node.title?.trim()) untitledDescendants.add(n.node.id);
            walk(n.children);
        });
        const findSubtree = (nodes: TreeNode[]): TreeNode | null => {
            for (const n of nodes) {
                if (n.node.id === id) return n;
                const found = findSubtree(n.children);
                if (found) return found;
            }
            return null;
        };
        const root = findSubtree(tree);
        if (root) walk(root.children);
        setGeneratingIds(prev => { const next = new Set(prev); untitledDescendants.forEach(d => next.add(d)); return next; });
        try {
            await onAutoTitleBranch(id);
        } finally {
            setGeneratingIds(prev => { const next = new Set(prev); untitledDescendants.forEach(d => next.delete(d)); return next; });
        }
    }, [onAutoTitleBranch, tree]);

    const handleToggleRearrangeMode = useCallback(() => {
        setIsRearrangeMode(prev => {
            if (prev) setSelectedIds(new Set()); // clear selection when exiting
            return !prev;
        });
    }, []);

    const handleToggleSelect = useCallback((id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    const handleDragStart = (id: string) => {
        // If dragging a selected node, move all selected; otherwise just this one
        const draggedIds = selectedIds.has(id) && selectedIds.size > 1
            ? Array.from(selectedIds)
            : [id];
        setDragState({ draggedId: id, draggedIds, overId: null, overPosition: null });
    };

    const handleDragOver = (id: string, position: 'before' | 'after' | 'onto') => {
        if (!dragState) return;
        if (dragState.draggedIds.includes(id)) return;
        // Prevent dropping onto a descendant of any dragged node
        for (const draggedId of dragState.draggedIds) {
            let cur = localNodes.find(n => n.id === id);
            while (cur?.parentId) {
                if (cur.parentId === draggedId) return;
                cur = localNodes.find(n => n.id === cur!.parentId);
            }
        }
        setDragState(prev => prev ? { ...prev, overId: id, overPosition: position } : null);
    };

    const handleDrop = () => {
        if (dragState?.overId && dragState.overPosition && !dragState.draggedIds.includes(dragState.overId)) {
            const { draggedId, draggedIds, overId, overPosition } = dragState;

            if (overPosition === 'onto') {
                // Reparent all dragged nodes to the target node
                setLocalNodes(prev => prev.map(n =>
                    draggedIds.includes(n.id) ? { ...n, parentId: overId } : n
                ));
                setSelectedIds(new Set());
            } else {
                // Reorder: move just the grabbed node before/after target (within same parent)
                setLocalNodes(prev => {
                    const dragged = prev.find(n => n.id === draggedId);
                    const target = prev.find(n => n.id === overId);
                    if (!dragged || !target) return prev;
                    const newParentId = target.parentId;
                    const withoutDragged = prev.filter(n => n.id !== draggedId);
                    const targetIndex = withoutDragged.findIndex(n => n.id === overId);
                    const insertIndex = overPosition === 'before' ? targetIndex : targetIndex + 1;
                    const updatedDragged = { ...dragged, parentId: newParentId };
                    return [
                        ...withoutDragged.slice(0, insertIndex),
                        updatedDragged,
                        ...withoutDragged.slice(insertIndex),
                    ];
                });
            }
            setIsDirty(true);
        }
        setDragState(null);
    };

    const handleDragEnd = () => setDragState(null);

    const handleApply = () => {
        onApplyOrder(localNodes);
        setIsDirty(false);
    };

    const handleReset = () => {
        setLocalNodes(nodes);
        setIsDirty(false);
    };

    const handleLinkSelectFromTree = useCallback((nodeId: string) => {
        if (viewingNb) {
            const targetNode = viewingNb.nodes.find(n => n.id === nodeId);
            onLinkSelect?.(nodeId, viewingNb.id, targetNode?.title || '', viewingNb.title);
        } else {
            onLinkSelect?.(nodeId);
        }
    }, [viewingNb, onLinkSelect]);

    return (
        <>
        <div className="h-full flex flex-col bg-white dark:bg-slate-900">
            <div className="flex items-center gap-2 px-3 py-2 border-b dark:border-slate-800 shrink-0">
                <span className="font-semibold text-sm text-gray-800 dark:text-gray-200 shrink-0">
                    {language === 'vietnamese' ? 'Cấu trúc' : 'Outline'}
                </span>
                {allNotebooks && allNotebooks.length > 1 && (
                    <div className="relative shrink-0">
                        <button
                            onClick={() => setNbPickerOpen(v => !v)}
                            title={language === 'vietnamese' ? 'Chuyển notebook' : 'Switch notebook'}
                            className={`p-1.5 rounded-lg transition-colors ${viewingNbId ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                        >
                            <BookOpen size={iconSize(isMobile, 13)} />
                        </button>
                        {nbPickerOpen && (
                            <>
                                <div className="fixed inset-0 z-[299]" onClick={() => setNbPickerOpen(false)} />
                                <div className="absolute left-0 top-full mt-1 z-[300] bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-slate-700 py-1 min-w-[200px] max-w-[280px] max-h-[320px] overflow-y-auto">
                                    {allNotebooks.map(nb => {
                                        const isCurrent = nb.id === (currentNotebookId || null);
                                        const isViewing = nb.id === viewingNbId;
                                        return (
                                            <button
                                                key={nb.id}
                                                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${isViewing ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : isCurrent ? 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700/50' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700/50'}`}
                                                onClick={() => {
                                                    const targetId = isCurrent ? null : nb.id;
                                                    setViewingNbId(targetId);
                                                    setFilterQuery('');
                                                    setNbPickerOpen(false);
                                                    if (targetId && onLoadNotebook) {
                                                        setLoadingNbId(targetId);
                                                        onLoadNotebook(targetId).finally(() => setLoadingNbId(null));
                                                    }
                                                }}
                                            >
                                                <span className="flex-1 truncate">{nb.title || (language === 'vietnamese' ? 'Không tên' : 'Untitled')}</span>
                                                {isCurrent && <span className="text-xs text-gray-400 shrink-0">{language === 'vietnamese' ? '(hiện tại)' : '(current)'}</span>}
                                                {isViewing && <Check size={12} className="text-indigo-500 shrink-0" />}
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                )}
                <div className="relative flex-1 flex items-center min-w-0">
                    <Search size={13} className="absolute left-2 text-gray-400 pointer-events-none" />
                    <input
                        ref={filterInputRef}
                        type="text"
                        value={filterQuery}
                        onChange={e => setFilterQuery(e.target.value)}
                        placeholder={language === 'vietnamese' ? 'Tìm...' : 'Search...'}
                        className="w-full pl-7 pr-7 py-1.5 text-sm bg-gray-100 dark:bg-slate-800 border border-transparent focus:border-blue-400 dark:focus:border-blue-500 rounded-lg outline-none text-gray-800 dark:text-gray-200 placeholder-gray-400 transition-colors"
                    />
                    {filterQuery && (
                        <button
                            onClick={() => { setFilterQuery(''); filterInputRef.current?.focus(); }}
                            className="absolute right-1.5 p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                            <X size={13} />
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-1 md:gap-0.5 shrink-0">
                    <button
                        onClick={handleExpandClick}
                        title={outlineLevel === 'collapsed'
                            ? (language === 'vietnamese' ? 'Mở rộng cấp 1' : 'Expand level 1')
                            : (language === 'vietnamese' ? 'Mở rộng tất cả' : 'Expand all')}
                        className="p-2.5 md:p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 active:bg-gray-200 dark:active:bg-slate-600 active:scale-95 transition-colors"
                    >
                        <ChevronsUpDown size={iconSize(isMobile, 14)} />
                    </button>
                    <button
                        onClick={handleCollapseClick}
                        title={outlineLevel === 'full'
                            ? (language === 'vietnamese' ? 'Thu gọn về cấp 1' : 'Collapse to level 1')
                            : (language === 'vietnamese' ? 'Thu gọn tất cả' : 'Collapse all')}
                        className="p-2.5 md:p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 active:bg-gray-200 dark:active:bg-slate-600 active:scale-95 transition-colors"
                    >
                        <ChevronsDownUp size={iconSize(isMobile, 14)} />
                    </button>
                    <button
                        onClick={handleToggleRearrangeMode}
                        title={isRearrangeMode
                            ? (language === 'vietnamese' ? 'Xong – thoát chế độ sắp xếp' : 'Done – exit rearrange mode')
                            : (language === 'vietnamese' ? 'Chế độ sắp xếp cấu trúc' : 'Rearrange mode')}
                        className={`p-2.5 md:p-1.5 rounded-lg active:scale-95 transition-colors ${
                            isRearrangeMode
                                ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/60'
                                : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                        }`}
                    >
                        <Layers size={iconSize(isMobile, 14)} />
                    </button>
                    <button
                        onClick={onClose}
                        className="p-2.5 md:p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 active:bg-gray-200 dark:active:bg-slate-600 active:scale-95 transition-colors"
                    >
                        <X size={iconSize(isMobile, 15)} />
                    </button>
                </div>
            </div>

            {viewingNb && (
                <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/20 border-b border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 text-xs font-semibold shrink-0">
                    <span className="flex items-center gap-1.5 min-w-0"><BookOpen size={11} className="shrink-0" /><span className="truncate">{viewingNb.title || (language === 'vietnamese' ? 'Không tên' : 'Untitled')}</span></span>
                    <button onClick={() => { setViewingNbId(null); setFilterQuery(''); }} className="shrink-0 p-0.5 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 rounded" title={language === 'vietnamese' ? 'Quay lại notebook hiện tại' : 'Back to current notebook'}><X size={11} /></button>
                </div>
            )}
            {pendingLink && onLinkNotes && (
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-teal-50 dark:bg-teal-900/20 border-b border-teal-200 dark:border-teal-800 text-teal-700 dark:text-teal-300 text-xs shrink-0">
                    <span className="flex items-start gap-1.5 min-w-0">
                        <Link2 size={12} className="shrink-0 mt-0.5" />
                        <span className="min-w-0">
                            <span className="font-semibold">{vi ? 'Đang nối từ' : 'Linking from'} “<span className="truncate">{pendingLink.title || (vi ? 'Không tên' : 'Untitled')}</span>”</span>
                            <span className="block text-teal-600/80 dark:text-teal-400/80">{vi ? 'Chuột phải vào note khác → “Nối liên kết tới đây”. Esc để huỷ.' : 'Right-click another note → “Link here”. Esc cancels.'}</span>
                        </span>
                    </span>
                    <button onClick={() => onPendingLinkChange?.(null)} className="shrink-0 p-0.5 hover:bg-teal-100 dark:hover:bg-teal-900/50 rounded" title={vi ? 'Huỷ liên kết đang chờ' : 'Cancel the waiting link'}><X size={12} /></button>
                </div>
            )}
            {linkMode && (
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-teal-50 dark:bg-teal-900/20 border-b border-teal-200 dark:border-teal-800 text-teal-700 dark:text-teal-300 text-xs font-semibold shrink-0">
                    <span className="flex items-center gap-1.5"><Link2 size={12} />{language === 'vietnamese' ? 'Chọn note để tạo liên kết' : 'Select a note to link'}{viewingNb ? ` · ${viewingNb.title}` : ''}</span>
                    <button onClick={onCancelLinkMode} className="p-0.5 hover:bg-teal-100 dark:hover:bg-teal-900/50 rounded"><X size={12} /></button>
                </div>
            )}

            <div className="flex-1 overflow-y-auto p-1.5 outline-none" tabIndex={0} onKeyDown={handleListKeyDown}>
                {loadingNbId === viewingNbId && viewingNbId ? (
                    <div className="flex flex-col items-center justify-center gap-2 mt-10 text-gray-400">
                        <Loader2 size={20} className="animate-spin text-indigo-400" />
                        <span className="text-sm">{language === 'vietnamese' ? 'Đang tải...' : 'Loading...'}</span>
                    </div>
                ) : tree.length === 0 ? (
                    <p className="text-center text-gray-400 text-sm mt-10">
                        {language === 'vietnamese' ? 'Chưa có ghi chú' : 'No notes yet'}
                    </p>
                ) : activeTree.length === 0 ? (
                    <p className="text-center text-gray-400 text-sm mt-10">
                        {language === 'vietnamese' ? 'Không tìm thấy kết quả' : 'No results found'}
                    </p>
                ) : (
                    activeTree.map(n => (
                        <OutlineItem
                            key={n.node.id}
                            treeNode={n}
                            collapsedIds={activeCollapsedIds}
                            onToggleCollapse={handleToggleCollapse}
                            onToggleCollapseAll={handleToggleCollapseAll}
                            generatingIds={generatingIds}
                            dragState={viewingNb ? null : dragState}
                            isRearrangeMode={viewingNb ? false : isRearrangeMode}
                            selectedIds={selectedIds}
                            onToggleSelect={handleToggleSelect}
                            onDragStart={handleDragStart}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onDragEnd={handleDragEnd}
                            onFocusNode={viewingNb ? () => {} : onFocusNode}
                            onExpandNode={viewingNb ? undefined : onExpandNode}
                            onContextMenu={viewingNb ? (pendingLink && onLinkNotes ? handleOpenContextMenu : () => {}) : handleOpenContextMenu}
                            activeNodeId={viewingNb ? null : activeNodeId}
                            onHoverEnter={handleItemHoverEnter}
                            onHoverLeave={handleItemHoverLeave}
                            linkMode={linkMode}
                            onLinkSelect={handleLinkSelectFromTree}
                            onCancelLinkMode={onCancelLinkMode}
                            editingId={editingId}
                            editingTitle={editingTitle}
                            onStartEdit={handleStartEdit}
                            onEditChange={handleEditChange}
                            onEditCommit={handleEditCommit}
                            onEditCancel={handleEditCancel}
                            filterQuery={filterQuery || undefined}
                            isMobile={isMobile}
                        />
                    ))
                )}
            </div>

            {contextMenu && (
                <>
                    <div className="fixed inset-0 z-[200]" onClick={() => setContextMenu(null)} />
                    <div
                        ref={contextMenuRef}
                        className="fixed z-[201] bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-slate-700 py-1 min-w-[200px]"
                        style={{ left: menuPos?.left ?? contextMenu.x, top: menuPos?.top ?? contextMenu.y, visibility: menuPos ? 'visible' : 'hidden' }}
                    >
                        {onLinkNotes && onPendingLinkChange && (() => {
                            const target = effectiveNodes.find(n => n.id === contextMenu.id);
                            const isSource = pendingLink?.id === contextMenu.id && !viewingNb;
                            const item = 'w-full px-3 py-2 text-left text-sm hover:bg-teal-50 dark:hover:bg-teal-900/20 flex items-center gap-2 transition-colors text-gray-700 dark:text-gray-200';
                            const short = (t: string) => (t.length > 28 ? t.slice(0, 28) + '…' : t) || (vi ? 'Không tên' : 'Untitled');
                            return (
                                <>
                                    {pendingLink && !isSource && (
                                        <button className={item} onClick={() => {
                                            onLinkNotes(pendingLink.id, contextMenu.id, viewingNb?.id, target?.title || '', viewingNb?.title);
                                            onPendingLinkChange(null);
                                            setContextMenu(null);
                                        }}>
                                            <Link2 size={13} className="text-teal-500" />
                                            <span>{vi ? 'Nối liên kết từ' : 'Link from'} “{short(pendingLink.title)}” {vi ? 'tới đây' : 'to here'}</span>
                                        </button>
                                    )}
                                    {pendingLink ? (
                                        <button className={item} onClick={() => { onPendingLinkChange(null); setContextMenu(null); }}>
                                            <X size={13} className="text-gray-400" />
                                            {vi ? 'Huỷ liên kết đang chờ' : 'Cancel the waiting link'}
                                        </button>
                                    ) : !viewingNb && (
                                        <button className={item} onClick={() => { onPendingLinkChange({ id: contextMenu.id, title: target?.title || '' }); setContextMenu(null); }}>
                                            <Link2 size={13} className="text-teal-500" />
                                            {vi ? 'Bắt đầu liên kết từ note này' : 'Start a link from this note'}
                                        </button>
                                    )}
                                    {!viewingNb && <div className="my-1 border-t border-gray-100 dark:border-slate-700" />}
                                </>
                            );
                        })()}
                        {!viewingNb && <>
                        {onCreateChildNote && (
                            <button
                                onClick={() => { onCreateChildNote(contextMenu.id); setContextMenu(null); }}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-emerald-50 dark:hover:bg-emerald-900/20 flex items-center gap-2 transition-colors text-gray-700 dark:text-gray-200"
                            >
                                <Plus size={13} className="text-emerald-500" />
                                {language === 'vietnamese' ? 'Tạo ghi chú con' : 'Create child note'}
                            </button>
                        )}
                        {onRenameNode && (
                            <button
                                onClick={() => {
                                    const node = localNodes.find(n => n.id === contextMenu.id);
                                    handleStartEdit(contextMenu.id, node?.title || '');
                                }}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center gap-2 transition-colors text-gray-700 dark:text-gray-200"
                            >
                                <Pencil size={13} className="text-blue-500" />
                                {language === 'vietnamese' ? 'Đổi tên' : 'Rename'}
                            </button>
                        )}
                        {((onCreateChildNote || onRenameNode) && (onAutoTitle || onAutoTitleBranch)) && (
                            <div className="my-1 border-t border-gray-100 dark:border-slate-700" />
                        )}
                        {onAutoTitle && (
                            <button
                                onClick={() => handleAutoTitle(contextMenu.id)}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-violet-50 dark:hover:bg-violet-900/20 flex items-center gap-2 transition-colors text-gray-700 dark:text-gray-200"
                            >
                                <Sparkles size={13} className="text-violet-500" />
                                {language === 'vietnamese' ? 'Tạo tiêu đề cho ghi chú này' : 'Generate title for this note'}
                            </button>
                        )}
                        {onAutoTitleBranch && (
                            <button
                                onClick={() => handleAutoTitleBranch(contextMenu.id)}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-violet-50 dark:hover:bg-violet-900/20 flex items-center gap-2 transition-colors text-gray-700 dark:text-gray-200"
                            >
                                <Sparkles size={13} className="text-violet-400" />
                                {language === 'vietnamese' ? 'Tạo tiêu đề cho tất cả ghi chú con' : 'Generate titles for all children'}
                            </button>
                        )}
                        <div className="my-1 border-t border-gray-100 dark:border-slate-700" />
                        <button
                            onClick={() => handleExpandBranch(contextMenu.id)}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-slate-700/50 flex items-center gap-2 transition-colors text-gray-700 dark:text-gray-200"
                        >
                            <ChevronDown size={13} className="text-gray-400" />
                            {language === 'vietnamese' ? 'Mở rộng tất cả ghi chú con' : 'Expand all children'}
                        </button>
                        <button
                            onClick={() => handleCollapseBranch(contextMenu.id)}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-slate-700/50 flex items-center gap-2 transition-colors text-gray-700 dark:text-gray-200"
                        >
                            <ChevronRight size={13} className="text-gray-400" />
                            {language === 'vietnamese' ? 'Thu gọn tất cả ghi chú con' : 'Collapse all children'}
                        </button>
                        </>}
                    </div>
                </>
            )}

            {isRearrangeMode && (
                <div className="shrink-0 px-3 py-2 border-t dark:border-slate-800 flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20">
                    <Layers size={12} className="text-blue-500 shrink-0" />
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium flex-1">
                        {selectedIds.size > 0
                            ? (language === 'vietnamese'
                                ? `Đã chọn ${selectedIds.size} — kéo thả vào node cha mới`
                                : `${selectedIds.size} selected — drag onto new parent`)
                            : (language === 'vietnamese'
                                ? 'Tick chọn các node, rồi kéo vào cha mới'
                                : 'Check nodes, then drag onto new parent')}
                    </span>
                    {selectedIds.size > 0 && (
                        <button
                            onClick={() => setSelectedIds(new Set())}
                            className="p-1 rounded text-blue-400 hover:text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                            title={language === 'vietnamese' ? 'Bỏ chọn tất cả' : 'Clear selection'}
                        >
                            <X size={13} />
                        </button>
                    )}
                </div>
            )}
            <div className="shrink-0 px-3 py-2.5 border-t dark:border-slate-800 flex gap-2">
                {isDirty && (
                    <button
                        onClick={handleReset}
                        className="flex-1 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    >
                        {language === 'vietnamese' ? 'Huỷ' : 'Reset'}
                    </button>
                )}
                <button
                    onClick={handleApply}
                    disabled={!isDirty}
                    className={`flex-1 py-1.5 text-sm font-medium rounded-lg flex items-center justify-center gap-1.5 transition-colors ${
                        isDirty
                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                            : 'bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                    }`}
                >
                    <Check size={14} />
                    {language === 'vietnamese' ? 'Áp dụng' : 'Apply'}
                </button>
            </div>
        </div>

        {/* Hover preview card */}
        {hoverPreview && (() => {
            const cardW = hoverExpanded ? 720 : 380;
            const cardH = hoverExpanded ? 900 : 480;
            const vpW = window.innerWidth;
            const vpH = window.innerHeight;
            let left = hoverPreview.left - cardW - 12;
            if (left < 8) left = hoverPreview.left + 20;
            if (left + cardW > vpW - 8) left = vpW - cardW - 8;
            let top = hoverPreview.top - 16;
            if (top + cardH > vpH - 8) top = vpH - cardH - 8;
            if (top < 8) top = 8;
            const content = hoverPreview.node.content || '';
            const stripped = content.replace(/\*\*|__|\*|_|~~|`{1,3}|#{1,6}\s?|\$\$?/g, '').trim();
            // Prev/next in full document (reading) order — lets the "Lùi/Tiến" bar below step
            // through the whole outline without closing this card or opening the note.
            const { prevId, nextId } = getAdjacentNoteIds(effectiveNodes, hoverPreview.node.id);
            const goToAdjacent = (id: string | null) => {
                if (!id) return;
                if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
                const target = effectiveNodes.find(n => n.id === id);
                if (target) setHoverPreview(prev => prev ? { ...prev, node: target } : prev);
            };
            return (
                <div
                    style={{ position: 'fixed', top, left, width: cardW, maxHeight: cardH, zIndex: 99999, transition: 'width 0.2s ease, max-height 0.2s ease' }}
                    className={`bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 relative ${hoverExpanded ? 'overflow-y-auto' : 'overflow-hidden flex flex-col'}`}
                    onMouseEnter={() => { if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current); }}
                    onMouseLeave={() => { if (!expandLockRef.current) { handleItemHoverLeave(); setHoverExpanded(false); } }}
                >
                    {onCreateLink && (
                        <button
                            onClick={() => { onCreateLink(hoverPreview.node.id, viewingNb?.id, hoverPreview.node.title || '', viewingNb?.title); }}
                            className="absolute top-2 right-24 z-10 p-1.5 rounded-md bg-black/20 hover:bg-teal-500/80 text-white transition-colors"
                            title={language === 'vietnamese' ? 'Tạo liên kết đến note này' : 'Create link to this note'}
                        >
                            <Link2 size={12} />
                        </button>
                    )}
                    <button
                        onClick={() => {
                            if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
                            expandLockRef.current = true;
                            const alreadyPinned = pinnedPreviews.some(p => p.node.id === hoverPreview.node.id);
                            if (!alreadyPinned) {
                                setPinnedPreviews(prev => [...prev, { id: `pin-${Date.now()}`, node: hoverPreview.node, x: left, y: top, expanded: hoverExpanded }]);
                            }
                            setHoverPreview(null);
                            setHoverExpanded(false);
                            setTimeout(() => { expandLockRef.current = false; }, 350);
                        }}
                        className="absolute top-2 right-16 z-10 p-1.5 rounded-md bg-black/20 hover:bg-indigo-500/80 text-white transition-colors"
                        title="Ghim cửa sổ này"
                    >
                        <Pin size={12} />
                    </button>
                    <button
                        onClick={() => {
                            expandLockRef.current = true;
                            if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
                            setHoverExpanded(v => !v);
                            setTimeout(() => { expandLockRef.current = false; }, 350);
                        }}
                        className="absolute top-2 right-9 z-10 p-1.5 rounded-md bg-black/20 hover:bg-black/40 text-white transition-colors"
                        title={hoverExpanded ? 'Thu nhỏ' : 'Phóng to'}
                    >
                        {hoverExpanded ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
                    </button>
                    <button
                        onClick={() => {
                            if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
                            setHoverPreview(null);
                            setHoverExpanded(false);
                        }}
                        className="absolute top-2 right-2 z-10 p-1.5 rounded-md bg-black/20 hover:bg-red-500/80 text-white transition-colors"
                        title={language === 'vietnamese' ? 'Đóng' : 'Close'}
                    >
                        <X size={12} />
                    </button>
                    <NodeImageSlider node={hoverPreview.node} expanded={hoverExpanded} />
                    <div className={hoverExpanded ? 'p-3' : 'flex flex-col flex-1 min-h-0 p-3'}>
                        {hoverPreview.node.title ? (
                            <div className="font-semibold text-sm text-gray-900 dark:text-white leading-snug shrink-0 mb-2 pr-16">{hoverPreview.node.title}</div>
                        ) : (
                            <div className="text-xs text-gray-400 italic shrink-0 mb-2">Untitled</div>
                        )}
                        {stripped ? (
                            <div
                                className={`${hoverExpanded ? 'text-sm' : 'text-xs'} text-gray-500 dark:text-gray-400 leading-relaxed ${hoverExpanded ? '' : 'overflow-y-auto flex-1'} pr-1 [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&>ul]:pl-3 [&>ol]:pl-3 [&>h1]:font-bold [&>h2]:font-semibold [&>h3]:font-semibold [&>strong]:font-semibold [&>em]:italic [&>pre]:bg-gray-100 [&>pre]:dark:bg-slate-700 [&>pre]:rounded [&>pre]:p-1 [&>code]:bg-gray-100 [&>code]:dark:bg-slate-700 [&>code]:rounded [&>code]:px-0.5`}
                                dangerouslySetInnerHTML={{ __html: parseMarkdown(content) }}
                            />
                        ) : (
                            <p className="text-xs text-gray-300 dark:text-gray-600 italic flex items-center gap-1">
                                <FileText size={10} /> {language === 'vietnamese' ? 'Không có nội dung' : 'No content'}
                            </p>
                        )}
                    </div>
                    {(prevId || nextId) && (
                        <div className="shrink-0 flex items-center justify-between border-t border-gray-100 dark:border-slate-700 px-2 py-1.5 bg-gray-50/80 dark:bg-slate-900/40">
                            <button
                                onClick={() => goToAdjacent(prevId)}
                                disabled={!prevId}
                                title={language === 'vietnamese' ? 'Note trước đó' : 'Previous note'}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-800 dark:hover:text-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                            >
                                <ChevronLeft size={13} /> {language === 'vietnamese' ? 'Lùi' : 'Back'}
                            </button>
                            <button
                                onClick={() => goToAdjacent(nextId)}
                                disabled={!nextId}
                                title={language === 'vietnamese' ? 'Note tiếp theo' : 'Next note'}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-800 dark:hover:text-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                            >
                                {language === 'vietnamese' ? 'Tiến' : 'Next'} <ChevronRight size={13} />
                            </button>
                        </div>
                    )}
                </div>
            );
        })()}

        {/* Pinned preview windows from OutlinePanel */}
        {pinnedPreviews.map(pinned => {
            const cardW = pinned.expanded ? 720 : 380;
            const cardH = pinned.expanded ? 900 : 480;
            const content = pinned.node.content || '';
            const stripped = content.replace(/\*\*|__|\*|_|~~|`{1,3}|#{1,6}\s?|\$\$?/g, '').trim();
            const { prevId: pinPrevId, nextId: pinNextId } = getAdjacentNoteIds(effectiveNodes, pinned.node.id);
            const goToAdjacentPinned = (id: string | null) => {
                if (!id) return;
                const target = effectiveNodes.find(n => n.id === id);
                if (target) setPinnedPreviews(prev => prev.map(p => p.id === pinned.id ? { ...p, node: target } : p));
            };
            return (
                <div
                    key={pinned.id}
                    style={{ position: 'fixed', top: pinned.y, left: pinned.x, width: cardW, maxHeight: cardH, zIndex: 99998, transition: 'width 0.2s ease, max-height 0.2s ease' }}
                    className={`bg-white dark:bg-slate-800 rounded-xl shadow-2xl border-2 border-indigo-200 dark:border-indigo-800 relative ${pinned.expanded ? 'overflow-y-auto' : 'overflow-hidden flex flex-col'}`}
                >
                    <div
                        className="h-7 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-slate-700 dark:to-slate-700 rounded-t-xl cursor-grab active:cursor-grabbing flex items-center px-2.5 gap-1.5 border-b border-indigo-100 dark:border-slate-600 shrink-0 select-none"
                        onMouseDown={(e) => {
                            draggingPinRef.current = { id: pinned.id, offsetX: e.clientX - pinned.x, offsetY: e.clientY - pinned.y };
                            e.preventDefault();
                        }}
                    >
                        <Pin size={10} className="text-indigo-400 shrink-0" />
                        <span className="text-[11px] text-indigo-500 dark:text-indigo-400 font-medium truncate flex-1">{pinned.node.title || 'Untitled'}</span>
                    </div>
                    {onCreateLink && (
                        <button
                            onClick={() => onCreateLink(pinned.node.id, viewingNb?.id, pinned.node.title || '', viewingNb?.title)}
                            className="absolute top-0.5 right-[3.5rem] z-10 p-1 rounded-md hover:bg-teal-100 dark:hover:bg-teal-900/30 text-teal-400 transition-colors"
                            title={language === 'vietnamese' ? 'Tạo liên kết đến note này' : 'Create link to this note'}
                        >
                            <Link2 size={12} />
                        </button>
                    )}
                    <button
                        onClick={() => setPinnedPreviews(prev => prev.map(p => p.id === pinned.id ? { ...p, expanded: !p.expanded } : p))}
                        className="absolute top-0.5 right-8 z-10 p-1 rounded-md hover:bg-indigo-100 dark:hover:bg-slate-600 text-indigo-400 transition-colors"
                        title={pinned.expanded ? 'Thu nhỏ' : 'Phóng to'}
                    >
                        {pinned.expanded ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
                    </button>
                    <button
                        onClick={() => setPinnedPreviews(prev => prev.filter(p => p.id !== pinned.id))}
                        className="absolute top-0.5 right-1 z-10 p-1 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 transition-colors"
                        title="Đóng"
                    >
                        <X size={12} />
                    </button>
                    <NodeImageSlider node={pinned.node} expanded={pinned.expanded} />
                    <div className={pinned.expanded ? 'p-3' : 'flex flex-col flex-1 min-h-0 p-3'}>
                        {pinned.node.title ? (
                            <div className="font-semibold text-sm text-gray-900 dark:text-white leading-snug shrink-0 mb-2">{pinned.node.title}</div>
                        ) : (
                            <div className="text-xs text-gray-400 italic shrink-0 mb-2">Untitled</div>
                        )}
                        {stripped ? (
                            <div
                                className={`${pinned.expanded ? 'text-sm' : 'text-xs'} text-gray-500 dark:text-gray-400 leading-relaxed ${pinned.expanded ? '' : 'overflow-y-auto flex-1'} pr-1 [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&>ul]:pl-3 [&>ol]:pl-3 [&>h1]:font-bold [&>h2]:font-semibold [&>h3]:font-semibold [&>strong]:font-semibold [&>em]:italic`}
                                dangerouslySetInnerHTML={{ __html: parseMarkdown(content) }}
                            />
                        ) : (
                            <p className="text-xs text-gray-300 dark:text-gray-600 italic flex items-center gap-1">
                                <FileText size={10} /> {language === 'vietnamese' ? 'Không có nội dung' : 'No content'}
                            </p>
                        )}
                    </div>
                    {(pinPrevId || pinNextId) && (
                        <div className="shrink-0 flex items-center justify-between border-t border-indigo-100 dark:border-slate-600 px-2 py-1.5 bg-indigo-50/60 dark:bg-slate-900/40">
                            <button
                                onClick={() => goToAdjacentPinned(pinPrevId)}
                                disabled={!pinPrevId}
                                title={language === 'vietnamese' ? 'Note trước đó' : 'Previous note'}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md text-indigo-500 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-slate-700 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                            >
                                <ChevronLeft size={13} /> {language === 'vietnamese' ? 'Lùi' : 'Back'}
                            </button>
                            <button
                                onClick={() => goToAdjacentPinned(pinNextId)}
                                disabled={!pinNextId}
                                title={language === 'vietnamese' ? 'Note tiếp theo' : 'Next note'}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md text-indigo-500 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-slate-700 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                            >
                                {language === 'vietnamese' ? 'Tiến' : 'Next'} <ChevronRight size={13} />
                            </button>
                        </div>
                    )}
                </div>
            );
        })}
        </>
    );
};
